import React, { useState } from 'react'
import { Modal, Form, Row, Col, Input, Upload, Button, message, Spin } from 'antd'
import { InboxOutlined, LoadingOutlined } from '@ant-design/icons'
import { useCaseViewContext } from '../../context'
import { ChatType } from './types'

const { Dragger } = Upload

const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp']
const ALLOWED_TYPES_LABEL = 'PNG, JPEG, GIF, or WebP'

export const ImageUploadModal = ({ visible, onCancel, onOk, chatContext }) => {
    const { caseDetails, provider, apiClasses } = useCaseViewContext()
    
    const [description, setDescription] = useState('')
    const [file, setFile] = useState(null)
    const [fileList, setFileList] = useState([])
    const [isUploadDisabled, setUploadDisabled] = useState(true)
    const [loading, setLoading] = useState(false)

    const layout = {
        labelCol: { span: 12 },
        wrapperCol: { span: 24 },
    }

    const handleImageUploadClick = async () => {
        if (!file || !description) {
            message.warning('Please select a file and add a description')
            return
        }

        setLoading(true)
        const formData = new FormData()
        formData.append('file', file)
        formData.append('description', description)
        formData.append('chatRoomId', caseDetails?.caseId || caseDetails?._id)
        formData.append('author', provider?.userId || provider?._id)
        formData.append('isPatient', false)

        try {
            if (!apiClasses?.ChatRoomAPI?.uploadChatImage) {
                message.error('Image upload functionality is not available')
                return
            }

            const response = await apiClasses.ChatRoomAPI.uploadChatImage(formData)
            if (response.data?.success) {
                message.success('Successfully uploaded image')
                setDescription('')
                setFile(null)
                setFileList([])
                setUploadDisabled(true)
                
                const eventName = chatContext === ChatType.PATIENT ? 'refreshPatientMessages' : 'refreshAdminMessages'
                const refreshEvent = new CustomEvent(eventName, {
                    detail: { caseId: caseDetails?.caseId || caseDetails?._id }
                })
                window.dispatchEvent(refreshEvent)
                
                onOk()
            } else {
                message.warning('Unable to upload image, please try again!')
            }
        } catch (error) {
            console.error('Upload error:', error)
            message.error('Error occurred while uploading image, Please try after a while!')
        } finally {
            setLoading(false)
        }
    }

    const handleChange = (info) => {
        const newFileList = info.fileList
        setFileList(newFileList)
        
        if (newFileList.length > 0) {
            const lastFile = newFileList[newFileList.length - 1]
            setFile(lastFile.originFileObj || lastFile)
            setUploadDisabled(!description.length)
        } else {
            setUploadDisabled(true)
        }
    }

    const fileUploadProps = {
        onRemove: () => {
            setFile(null)
            setFileList([])
            setUploadDisabled(true)
        },
        beforeUpload: (file) => {
            if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
                message.error(`Unsupported file type. Please upload an image (${ALLOWED_TYPES_LABEL}).`)
                return Upload.LIST_IGNORE
            }
            return false
        },
        onChange: handleChange,
        multiple: false,
        accept: ALLOWED_IMAGE_TYPES.join(','),
    }

    const handleDescriptionChange = (event) => {
        const description = event.target.value
        setDescription(description)
        setUploadDisabled(!description.length || !file)
    }

    const handleCancel = () => {
        setDescription('')
        setFile(null)
        setFileList([])
        setUploadDisabled(true)
        onCancel()
    }

    return (
        <Modal
            title="Upload Image File"
            open={visible}
            footer={null}
            onCancel={handleCancel}
            width={720}
            destroyOnClose
            maskClosable={false}
        >
            <Spin
                size="large"
                spinning={loading}
                indicator={<LoadingOutlined spin />}
            >
                <Form {...layout} layout="vertical" name="chat-image-upload">
                    <Row gutter={24}>
                        <Col span={24}>
                            <Form.Item label="Image Description">
                                <Input 
                                    id="image-upload-description-input"
                                    size="large" 
                                    value={description}
                                    onChange={handleDescriptionChange}
                                    placeholder="Enter a description for the image"
                                />
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item label="Upload image">
                                <Dragger
                                    id="image-upload-dragger"
                                    {...fileUploadProps}
                                    fileList={fileList}
                                    className="chat-image-upload-dragger"
                                >
                                    <p className="ant-upload-drag-icon">
                                        <InboxOutlined />
                                    </p>
                                    <p className="ant-upload-text">
                                        Click or drag the image file to this area to upload
                                    </p>
                                    <p className="ant-upload-hint">
                                        Support for a single file upload. Please also add a
                                        description for image to be viewed in the chat
                                    </p>
                                </Dragger>
                            </Form.Item>
                        </Col>
                        
                        <Col className="chat-image-upload-button-wrapper" span={24}>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                                <Button id="image-upload-cancel-btn" onClick={handleCancel}>
                                    Cancel
                                </Button>
                                <Button 
                                    id="image-upload-submit-btn"
                                    type="primary" 
                                    onClick={handleImageUploadClick}
                                    disabled={isUploadDisabled}
                                    loading={loading}
                                >
                                    Upload
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </Modal>
    )
}
